import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { US_STATES, CA_PROVINCES, COUNTRIES } from "../../utils/locationData";

/*
 * Country and state pickers for a shipping address.
 *
 * We ship to the US and Canada only, so both fields are closed lists rather than
 * free text. That is not just a convenience: `isValidPostalCode` only enforces
 * the 5-digit rule when the country is "US", so a typo in a free-text country
 * box silently disabled ZIP validation altogether.
 *
 * Lifted out of InfinityCheckout, which had the only working copy, so the
 * address book and checkout cannot drift apart.
 */

/** The provinces/states that belong to a country code. */
export const statesFor = (country) => (country === "CA" ? CA_PROVINCES : US_STATES);

export function CountrySelect({ value, onChange, className, invalid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn(className, invalid && "border-red-500")}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {COUNTRIES.map((c) => (
          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/*
 * Searchable because the list is 50+ entries long — a plain <select> means
 * scrolling to find "Pennsylvania". Matching is on the label, so a user types
 * the state name they know rather than its two-letter code.
 */
export function StateCombobox({ country, value, onChange, className, invalid, placeholder = "Select" }) {
  const [open, setOpen] = useState(false);
  const states = statesFor(country);
  const selected = states.find((s) => s.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn("w-full justify-between font-normal", className, invalid && "border-red-500")}
        >
          {selected?.label || placeholder}
          <ChevronsUpDown className="w-4 h-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder="Search state…" />
          <CommandList>
            <CommandEmpty>None found.</CommandEmpty>
            <CommandGroup>
              {states.map((s) => (
                <CommandItem key={s.value} value={s.label} onSelect={() => { onChange(s.value); setOpen(false); }}>
                  <Check className={cn("mr-2 h-4 w-4", value === s.value ? "opacity-100" : "opacity-0")} />
                  {s.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
